import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import AdminDashboard from "@/pages/admin-dashboard/AdminDashboard";
import LazyRoute from "./LazyRoute";
import PrivateRoute from "./PrivateRoute";
import React from "react";
import RoleBasedRoutes from "./RoleBasedRoutes";
import RouteNames from "./routeNames";
import SuperAdminLoginPage from "@/pages/admin-login-page";
import UsersList from "@/pages/admin-dashboard/user-list";
import { authSelectors } from "@/redux/auth";
import { useAppSelector } from "@/redux";

/**
 * All routes are placed here. If you wish to lazy load a page, refer to the example below
 * @example ```js
 * const UserDashboard = React.lazy(() => import("../panels/user/pages/Dashboard/index.tsx"))
 * .
 * .
 * .
 * <Route path="dashboard" element={<LazyRoute element={<UserDashboard />} />} />
 * ```
 * **Important note:**
 * You must export default the page and import that default export with `React.lazy` in order for lazy loading to work.
 */

//Login
const Login = React.lazy(() => import("@/pages/login-page"));

//ForgetPassword Pages
const ForgetPassword = React.lazy(
  () => import("@/pages/forget-password/email")
);
//Register
const Register = React.lazy(() => import("@/pages/register-page"));

//Projects
// const Projects = React.lazy(() => import("@/pages/dashboard/projects"));
// const SingleProject = React.lazy(
//   () => import("@/pages/dashboard/projects/single")
// );

const ProjectsV2 = React.lazy(() => import("@/pages/dashboard/projects-v2"));
const SingleProjectV2 = React.lazy(
  () => import("@/pages/dashboard/projects-v2/single")
);
const SingleProjectV2Module = React.lazy(
  () => import("@/pages/dashboard/projects-v2/single/single-module")
);
const SingleProjectV2ModuleThreeJS = React.lazy(
  () => import("@/pages/dashboard/projects-v2/single/single-module/threejs")
);
const SingleProjectV2ModuleResult = React.lazy(
  () => import("@/pages/dashboard/projects-v2/single/single-module/page/result")
);

const SingleProjectV2Raycaster = React.lazy(
  () => import("@/pages/dashboard/projects-v2/single/raycaster")
);

const DemoSunPath = React.lazy(() => import("@/pages/demo-sun-path"));

const PsychrometricChartPage = React.lazy(
  () => import("@/pages/dashboard/psychrometric-chart")
);

const NaturalVentilationPage = React.lazy(
  () => import("@/pages/dashboard/natural-ventilation")
);

const NaturalVentilationHeatmapPage = React.lazy(
  () => import("@/pages/dashboard/natural-ventilation-heatmap")
);

const RouterConfig = () => {
  const isAuthenticated = useAppSelector(authSelectors.isAuthSelector);
  const { login, signUp, dashboard, forgetPassword, superAdminLogin } =
    RouteNames;
  return (
    <Routes>
      <Route path={"/"}>
        <Route
          index
          element={
            <LazyRoute
              element={
                <Navigate to={isAuthenticated ? dashboard.index : login} />
              }
            />
          }
        />
        <Route path={login} element={<LazyRoute element={<Login />} />} />
        <Route
          path={superAdminLogin}
          element={<LazyRoute element={<SuperAdminLoginPage />} />}
        />
        <Route path={signUp} element={<LazyRoute element={<Register />} />} />
        <Route
          path="demo-sun-path"
          element={<LazyRoute element={<DemoSunPath />} />}
        />
        <Route path={forgetPassword.index}>
          <Route index element={<LazyRoute element={<ForgetPassword />} />} />
          <Route
            path={forgetPassword.withToken}
            element={<LazyRoute element={<ForgetPassword />} />}
          />
        </Route>
      </Route>
      <Route
        element={<LazyRoute element={<PrivateRoute element={<Outlet />} />} />}
      >
        <Route
          path={dashboard.adminDashboard}
          element={
            <RoleBasedRoutes
              element={<LazyRoute element={<AdminDashboard />} />}
            />
          }
        />

        <Route
          path={dashboard.users}
          element={
            <RoleBasedRoutes element={<LazyRoute element={<UsersList />} />} />
          }
        />

        <Route path={dashboard.index}>
          <Route
            index
            element={
              <LazyRoute element={<Navigate to={dashboard.projects.index} />} />
            }
          />

          <Route
            path={`/dashboard/projects`}
            element={<LazyRoute element={<ProjectsV2 />} />}
          />
          <Route
            path={`/dashboard/projects/:id`}
            element={<LazyRoute element={<SingleProjectV2 />} />}
          />

          <Route
            path={`/dashboard/projects/:id/:module`}
            element={<LazyRoute element={<SingleProjectV2Module />} />}
          />
          <Route
            path={`/dashboard/projects/:id/:module/result`}
            element={<LazyRoute element={<SingleProjectV2ModuleResult />} />}
          />
          <Route
            path={`/dashboard/projects/:id/threejs`}
            element={<LazyRoute element={<SingleProjectV2ModuleThreeJS />} />}
          />

          <Route
            path={`/dashboard/projects/:id/raycaster`}
            element={<LazyRoute element={<SingleProjectV2Raycaster />} />}
          />

          <Route
            path={`/dashboard/projects/:id/psychrometric-chart`}
            element={<LazyRoute element={<PsychrometricChartPage />} />}
          />

          <Route
            path={`/dashboard/projects/:id/natural-ventilation`}
            element={<LazyRoute element={<NaturalVentilationPage />} />}
          />

          <Route
            path={`/dashboard/projects/:id/natural-ventilation-heatmap`}
            element={<LazyRoute element={<NaturalVentilationHeatmapPage />} />}
          />
        </Route>
      </Route>
    </Routes>
  );
};

export default RouterConfig;
